import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { initials } from './Avatar';

const HeaderContainer = styled.div`
  background: linear-gradient(135deg, #4677b5 0%, #3d6aa5 55%, #335d93 100%);
  color: white;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

/* Bare bolt sitting directly on the gradient (no tile) */
const Logo = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 2px 6px rgba(40, 20, 90, 0.35));
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

/* Wordmark: italic caps with slight tracking (margin-right cancels the trailing letter-spacing) */
const BrandName = styled.div`
  font-size: 21px;
  font-weight: 700;
  font-style: italic;
  letter-spacing: 0.02em;
  margin-right: -0.02em;
  line-height: 1;
`;

const SubTitle = styled.div`
  margin-top: 4px;
  font-size: 11.5px;
  line-height: 1.2;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Aura mark: a white lightning bolt for "instant answers", drawn straight on the header gradient
const AuraMark = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M13.5 2 4.5 13.2c-.4.5 0 1.3.6 1.3H11l-1.2 7.1c-.1.7.8 1.1 1.3.5l8.4-11c.4-.5 0-1.3-.6-1.3h-5.6l1.3-7.1c.1-.7-.8-1.1-1.1-.7Z"
      fill="#fff"
    />
  </svg>
);

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255,255,255,0.3);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

// --- Live chat: the header shows who the client is talking to ---

// While the agent is live the green dot pulses, like an "on air" light
const livePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.55); }
  70% { box-shadow: 0 0 0 5px rgba(22, 163, 74, 0); }
  100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
`;

const AgentPhoto = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  background: ${props => (props.$live ? '#dcf3e6' : '#e7edf3')};
  color: ${props => (props.$live ? '#166534' : '#275386')};
`;

const StatusDot = styled.span`
  position: absolute;
  right: -1px;
  bottom: 1px;
  width: 11px;
  height: 11px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid #3d6aa5;
  background: ${props => (props.$live ? '#16a34a' : '#f59e0b')};
  ${props => props.$live && css`
    animation: ${livePulse} 1.6s ease-out infinite;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const AgentName = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ViaAura = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: 11px;
  opacity: 0.75;
`;

const EndChatButton = styled.button`
  height: 32px;
  padding: 0 12px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 16px;
  background: transparent;
  color: white;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const MiniBolt = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M13.5 2 4.5 13.2c-.4.5 0 1.3.6 1.3H11l-1.2 7.1c-.1.7.8 1.1 1.3.5l8.4-11c.4-.5 0-1.3-.6-1.3h-5.6l1.3-7.1c.1-.7-.8-1.1-1.1-.7Z"
      fill="#fff"
    />
  </svg>
);

const LiveChatHeader = ({ agentName, onEndChat, onMinimise }) => {
  // Live chats are only assigned to agents who are online, so the agent shows as online from
  // assignment (the pickup timeout still runs in the background until they actually reply)
  const live = true;
  const name = agentName || '5C Support';
  return (
    <HeaderContainer>
      <AgentPhoto $live={live} aria-hidden="true">
        {initials(name)}
        <StatusDot $live={live} />
      </AgentPhoto>
      <HeaderText>
        <AgentName>{name}</AgentName>
        <SubTitle>5C Support · Online</SubTitle>
        <ViaAura><MiniBolt /> via Aura</ViaAura>
      </HeaderText>
      <ActionButtons>
        <EndChatButton type="button" onClick={onEndChat}>End chat</EndChatButton>
        {/* Hides Aura without ending the chat; reopening returns to it */}
        <ActionButton type="button" onClick={onMinimise} title="Minimise" aria-label="Minimise">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M6 12h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        </ActionButton>
      </ActionButtons>
    </HeaderContainer>
  );
};

const Header = ({ onNewSession, onClose, liveChat, onEndChat, onMinimise }) => {
  if (liveChat) {
    return <LiveChatHeader {...liveChat} onEndChat={onEndChat} onMinimise={onMinimise} />;
  }
  return (
    <HeaderContainer>
      <Logo>
        <AuraMark />
      </Logo>
      <HeaderText>
        <BrandName>AURA</BrandName>
        <SubTitle>AI-powered assistant by 5C</SubTitle>
      </HeaderText>
      <ActionButtons>
        {/* <ActionButton onClick={onNewSession} title="New Session">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </ActionButton> */}
        <ActionButton onClick={onClose} title="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </ActionButton>
      </ActionButtons>
    </HeaderContainer>
  );
};

export default Header; 